import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.js';

type Conversation = {
  id: string;
  title: string;
  updatedAt: string;
  expiresAt: string;
};
type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolMetadata: Array<{ name: string; success: boolean }> | null;
  createdAt: string;
};

export function ForecastAgent() {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const conversations = useQuery({
    queryKey: ['forecast-agent', 'conversations'],
    queryFn: () => api<{ data: Conversation[] }>('/api/v1/agent/conversations'),
    enabled: open,
  });
  const effectiveConversationId = conversationId ?? conversations.data?.data[0]?.id ?? null;
  const messages = useQuery({
    queryKey: ['forecast-agent', 'messages', effectiveConversationId],
    queryFn: () =>
      api<{ data: Message[] }>(`/api/v1/agent/conversations/${effectiveConversationId}/messages`),
    enabled: open && Boolean(effectiveConversationId),
  });
  const mutation = useMutation({
    mutationFn: () =>
      api<{
        data: {
          conversationId: string;
          message: string;
          expiresAt: string;
        };
      }>('/api/v1/agent/messages', {
        method: 'POST',
        body: JSON.stringify({
          conversationId: effectiveConversationId,
          message,
          locale: i18n.language.startsWith('en') ? 'en' : 'fr',
        }),
      }),
    onSuccess: (result) => {
      setMessage('');
      setConversationId(result.data.conversationId);
      void queryClient.invalidateQueries({ queryKey: ['forecast-agent'] });
    },
  });
  return (
    <>
      <button
        className="forecast-agent-launcher"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="forecast-agent-panel"
      >
        ✦<span>Forecast Agent</span>
      </button>
      {open && (
        <aside id="forecast-agent-panel" className="forecast-agent-panel">
          <header>
            <div>
              <strong>✦ Forecast Agent</strong>
              <small>Lecture seule · historique 30 jours</small>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Fermer">
              ×
            </button>
          </header>
          <div className="forecast-agent-thread-controls">
            <select
              value={effectiveConversationId ?? ''}
              onChange={(event) => setConversationId(event.target.value || null)}
            >
              <option value="">Nouvelle conversation</option>
              {conversations.data?.data.map((conversation) => (
                <option key={conversation.id} value={conversation.id}>
                  {conversation.title}
                </option>
              ))}
            </select>
            <button onClick={() => setConversationId(null)}>Nouveau</button>
          </div>
          <div className="forecast-agent-messages" aria-live="polite">
            {!effectiveConversationId && (
              <div className="forecast-agent-empty">
                <strong>Que veux-tu savoir ?</strong>
                <p>
                  Demande les surcharges, la capacité d’une personne ou le dernier forecast d’un
                  projet.
                </p>
              </div>
            )}
            {messages.data?.data.map((item) => (
              <article key={item.id} className={`forecast-message forecast-message--${item.role}`}>
                <small>{item.role === 'user' ? 'Vous' : 'Forecast Agent'}</small>
                <p>{item.content}</p>
                {item.role === 'assistant' && item.toolMetadata?.length ? (
                  <details>
                    <summary>Sources consultées</summary>
                    <ul>
                      {item.toolMetadata.map((tool, index) => (
                        <li key={`${tool.name}:${index}`}>
                          {tool.name} · {tool.success ? 'consultée' : 'indisponible'}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </article>
            ))}
            {mutation.isPending && (
              <article className="forecast-message forecast-message--assistant">
                <small>Forecast Agent</small>
                <p>Analyse des données autorisées…</p>
              </article>
            )}
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (message.trim()) mutation.mutate();
            }}
          >
            <textarea
              value={message}
              maxLength={8_000}
              rows={3}
              placeholder="Ex. Qui est en surcharge dans les 12 prochaines semaines ?"
              onChange={(event) => setMessage(event.target.value)}
            />
            <button disabled={!message.trim() || mutation.isPending} type="submit">
              Envoyer
            </button>
          </form>
          {mutation.error && <div className="error-panel">{mutation.error.message}</div>}
        </aside>
      )}
    </>
  );
}
